import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface User {
  id: number;
  email: string;
  username: string;
  name: string;
  surname: string;
  patronymic?: string;
  role: string;
  position: string;
  airport?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  email: string;
  username: string;
  password: string;
  name: string;
  surname: string;
  patronymic?: string;
  role?: string;
  position: string;
  airport?: string;
  enabled: boolean;
}

export interface UpdateUserRequest {
  email?: string;
  username?: string;
  password?: string;
  name?: string;
  surname?: string;
  patronymic?: string;
  role?: string;
  position: string;
  airport?: string;
  enabled?: boolean;
}

export interface UserSearchRequest {
  email?: string;
  username?: string;
  name?: string;
  surname?: string;
  role?: string;
  position?: string;
  airport?: string;
  enabled?: boolean;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class UserService {
  private baseUrl = '/api/users';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех пользователей с пагинацией
  async getUsers(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение пользователя по ID
  async getUserById(id: number): Promise<User> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение текущего пользователя
  async getCurrentUser(): Promise<User> {
    const url = getDataApiUrl(`${this.baseUrl}/me`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание нового пользователя
  async createUser(userData: CreateUserRequest): Promise<User> {
    const url = getDataApiUrl(this.baseUrl);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление пользователя
  async updateUser(id: number, userData: UpdateUserRequest): Promise<User> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление пользователя
  async deleteUser(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Поиск пользователей
  async searchUsers(searchData: UserSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение пользователей по роли
  async getUsersByRole(role: string): Promise<User[]> {
    const url = getDataApiUrl(`${this.baseUrl}/role/${encodeURIComponent(role)}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение пользователей по аэропорту
  async getUsersByAirport(airport: string): Promise<User[]> {
    const url = getDataApiUrl(`${this.baseUrl}/airport/${encodeURIComponent(airport)}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Изменение пароля пользователя
  async changePassword(userId: number, oldPassword: string, newPassword: string): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${userId}/password`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ oldPassword, newPassword })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Сброс пароля пользователя
  async resetPassword(userId: number): Promise<{ temporaryPassword: string }> {
    const url = getDataApiUrl(`${this.baseUrl}/${userId}/password/reset`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Активация/деактивация пользователя
  async toggleUserStatus(userId: number, enabled: boolean): Promise<User> {
    const url = getDataApiUrl(`${this.baseUrl}/${userId}/status`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ enabled })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение статистики пользователей
  async getUserStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    usersByRole: Record<string, number>;
    usersByAirport: Record<string, number>;
  }> {
    const url = getDataApiUrl(`${this.baseUrl}/stats`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
}

const userService = new UserService();
export default userService; 